import { ref } from 'valtio';

export const ACTION_STACK_LIMIT = 45;

export interface ActionStackState<TEntry> {
  entries: TEntry[];
  index: number;
}

export function createInitialActionStackState<
  TEntry,
>(): ActionStackState<TEntry> {
  return {
    entries: [],
    index: -1,
  };
}

export function appendActionEntry<TEntry>(
  actionStack: ActionStackState<TEntry>,
  entry: TEntry,
  maxLength = ACTION_STACK_LIMIT,
): ActionStackState<TEntry> {
  const nextEntries = actionStack.entries.slice(0, actionStack.index + 1);
  nextEntries.push(entry);

  if (nextEntries.length > maxLength) {
    nextEntries.splice(0, nextEntries.length - maxLength);
  }

  return {
    entries: nextEntries,
    index: nextEntries.length - 1,
  };
}

export function appendActionEntryInPlace<TEntry extends object>(
  actionStack: ActionStackState<TEntry>,
  entry: TEntry,
  maxLength = ACTION_STACK_LIMIT,
): ActionStackState<TEntry> {
  const entries = actionStack.entries;
  entries.splice(actionStack.index + 1);
  entries.push(ref(entry));

  if (entries.length > maxLength) {
    entries.splice(0, entries.length - maxLength);
  }

  actionStack.index = entries.length - 1;
  return actionStack;
}

export function canUndoAction<TEntry>(actionStack: ActionStackState<TEntry>) {
  return actionStack.index >= 0;
}

export function canRedoAction<TEntry>(actionStack: ActionStackState<TEntry>) {
  return actionStack.index < actionStack.entries.length - 1;
}
